"use strict";
/**
 * @fileoverview Module for GL context related operations.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.glRender = exports.cubeTextureFromFramebuffer = exports.texture2DFromFramebuffer = exports.renderBuffer = exports.sendCubeTexture = exports.send2DTexture = exports.cubeTexure = exports.texture2d = exports.isVideo = exports.newTextureOffset = exports.sendVectorUniform = exports.sendMatrixUniform = exports.sendBytesUniform = exports.UniformType = exports.sendMatrixAttribute = exports.sendAttribute = exports.sendIndices = exports.glProgram = exports.glContext = void 0;
const math_1 = require("./math");
/**
 * Get the current WebGL context.
 * If this is the first time you
 */
exports.glContext = (canvas) => {
    const gl = canvas.getContext('webgl', { preserveDrawingBuffer: true })
        || canvas.getContext('experimental-webgl', { preserveDrawingBuffer: true });
    gl.enable(gl.DEPTH_TEST);
    gl.depthFunc(gl.LEQUAL);
    return gl;
};
/**
 * Compile one of the shaders.
 */
const compileShader = (gl, shader, src) => {
    gl.shaderSource(shader, src);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        throw new Error(`Shader failed to compile: ${gl.getShaderInfoLog(shader)}`);
    }
    return shader;
};
/**
 * Create and compile a shader program.
 */
exports.glProgram = (gl, vertexSrc, fragmentSrc) => {
    const vertexShader = compileShader(gl, gl.createShader(WebGLRenderingContext.VERTEX_SHADER), vertexSrc);
    const fragmentShader = compileShader(gl, gl.createShader(WebGLRenderingContext.FRAGMENT_SHADER), fragmentSrc);
    const result = gl.createProgram();
    gl.attachShader(result, vertexShader);
    gl.attachShader(result, fragmentShader);
    gl.linkProgram(result);
    if (!gl.getProgramParameter(result, gl.LINK_STATUS)) {
        throw new Error(`Shader failed to compile: ${gl.getProgramInfoLog(result)}`);
    }
    return result;
};
/**
 * Send indices to the element array buffer.
 */
exports.sendIndices = (gl, indices) => {
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, gl.createBuffer());
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, indices, gl.STATIC_DRAW);
};
/**
 * Send an attribute to a shader's array buffer.
 */
exports.sendAttribute = (gl, program, buffer, name, data, size) => {
    const loc = gl.getAttribLocation(program, name);
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.vertexAttribPointer(loc, size, gl.FLOAT, false, 0, 0);
    gl.enableVertexAttribArray(loc);
    gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);
};
/**
 * Send a matrix attribute to a shader's array buffer.
 *
 * Matrix attributes are sent as vectors in column-major
 * order. Each element in "data" is the data for one column
 * for each vertex.
 */
exports.sendMatrixAttribute = (gl, program, buffer, name, data, dimension) => {
    const loc = gl.getAttribLocation(program, name);
    for (let i = 0; i < dimension; i++) {
        gl.enableVertexAttribArray(loc + i);
        gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
        gl.bufferData(gl.ARRAY_BUFFER, data[i], gl.STATIC_DRAW);
        gl.vertexAttribPointer(loc + i, dimension, gl.FLOAT, false, 0, 0);
    }
};
/**
 * Different possible types of uniforms.
 */
var UniformType;
(function (UniformType) {
    UniformType["BOOLEAN"] = "boolean";
    UniformType["FLOAT"] = "float";
    UniformType["INTEGER"] = "integer";
    UniformType["VECTOR"] = "vector";
    UniformType["MATRIX"] = "matrix";
    UniformType["TEXTURE"] = "texture";
})(UniformType = exports.UniformType || (exports.UniformType = {}));
/**
 * Send a uniform to a shader that can be represented
 * with just a few bytes (boolean, float, integer).
 */
exports.sendBytesUniform = (gl, program, name, type, data) => {
    const loc = gl.getUniformLocation(program, name);
    switch (type) {
        case UniformType.BOOLEAN:
            gl.uniform1i(loc, data);
            break;
        case UniformType.FLOAT:
            gl.uniform1f(loc, data);
            break;
        case UniformType.INTEGER:
            gl.uniform1i(loc, data);
            break;
        default:
            throw new Error(`Unexpected uniform type: ${type}`);
    }
};
/**
 * Sends a vector uniform to a shader.
 */
exports.sendMatrixUniform = (gl, program, name, dimension, data) => {
    const loc = gl.getUniformLocation(program, name);
    switch (dimension) {
        case 2:
            gl.uniformMatrix2fv(loc, false, data);
            break;
        case 3:
            gl.uniformMatrix3fv(loc, false, data);
            break;
        case 4:
            gl.uniformMatrix4fv(loc, false, data);
            break;
    }
};
/**
 * Sends a vector uniform to a shader.
 */
exports.sendVectorUniform = (gl, program, name, dimension, data) => {
    const loc = gl.getUniformLocation(program, name);
    switch (dimension) {
        case 2:
            gl.uniform2fv(loc, data);
            break;
        case 3:
            gl.uniform3fv(loc, data);
            break;
        case 4:
            gl.uniform4fv(loc, data);
            break;
        default:
            throw new Error(`Unexpected dimension: ${dimension}`);
    }
};
const textureOffsets = new WeakMap();
/**
 * Get the next available address for textures.
 */
exports.newTextureOffset = (program, name) => {
    const offsets = textureOffsets.get(program) || [];
    for (let i = 0; i < offsets.length; i++) {
        if (offsets[i] === name)
            return i;
    }
    const curOffset = offsets.length;
    if (curOffset === 32) {
        throw new Error('Already at maximum number of textures for this program');
    }
    offsets.push(name);
    if (!curOffset)
        textureOffsets.set(program, offsets);
    return curOffset;
};
/**
 * Get if texture data is for a video source.
 */
exports.isVideo = (data) => {
    if (math_1.isCube(data)) {
        return Object.keys(data).some((k) => data[k] instanceof HTMLVideoElement);
    }
    return data instanceof HTMLVideoElement;
};
/**
 * Create a 2D texture with the provided data.
 */
exports.texture2d = (gl, data) => {
    const texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, data);
    if (!exports.isVideo(data) && math_1.isPowerOfTwo(data.width) && math_1.isPowerOfTwo(data.height)) {
        gl.generateMipmap(gl.TEXTURE_2D);
    }
    else {
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    }
    return texture;
};
const glTexCubeMapFaces = {
    posx: WebGLRenderingContext.TEXTURE_CUBE_MAP_POSITIVE_X,
    negx: WebGLRenderingContext.TEXTURE_CUBE_MAP_NEGATIVE_X,
    posy: WebGLRenderingContext.TEXTURE_CUBE_MAP_POSITIVE_Y,
    negy: WebGLRenderingContext.TEXTURE_CUBE_MAP_NEGATIVE_Y,
    posz: WebGLRenderingContext.TEXTURE_CUBE_MAP_POSITIVE_Z,
    negz: WebGLRenderingContext.TEXTURE_CUBE_MAP_NEGATIVE_Z,
};
/**
 * Checks if the size of each face of a cube texture is a power of two.
 */
const cubeFacesArePowersOfTwo = (data) => {
    for (const cf of math_1.cubeFaces()) {
        if (!math_1.isPowerOfTwo(data[cf].width) || !math_1.isPowerOfTwo(data[cf].height)) {
            return false;
        }
    }
    return true;
};
/**
 * Create a cube texture with the provided data.
 */
exports.cubeTexure = (gl, data) => {
    const texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_CUBE_MAP, texture);
    for (const cf of math_1.cubeFaces()) {
        gl.texImage2D(glTexCubeMapFaces[cf], 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, data[cf]);
    }
    if (!exports.isVideo(data) && cubeFacesArePowersOfTwo(data)) {
        gl.generateMipmap(gl.TEXTURE_CUBE_MAP);
    }
    else {
        gl.texParameteri(gl.TEXTURE_CUBE_MAP, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_CUBE_MAP, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    }
    gl.texParameteri(gl.TEXTURE_CUBE_MAP, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
    return texture;
};
/**
 * Send a texture to a shader as a uniform.
 */
const sendTexture = (target) => (gl, program, name, offset, texture) => {
    const loc = gl.getUniformLocation(program, name);
    gl.uniform1i(loc, offset);
    gl.activeTexture(gl.TEXTURE0 + offset);
    gl.bindTexture(target, texture);
};
/**
 * Send a 2D texture as a uniform to a shader.
 */
exports.send2DTexture = sendTexture(WebGLRenderingContext.TEXTURE_2D);
/**
 * Send a cube texture as a uniform to a shader.
 */
exports.sendCubeTexture = sendTexture(WebGLRenderingContext.TEXTURE_CUBE_MAP);
/**
 * Creates a render buffer from a given frame buffer.
 */
exports.renderBuffer = (gl, fBuffer, width, height) => {
    const rBuffer = gl.createRenderbuffer();
    gl.bindFramebuffer(gl.FRAMEBUFFER, fBuffer);
    gl.bindRenderbuffer(gl.RENDERBUFFER, rBuffer);
    gl.renderbufferStorage(gl.RENDERBUFFER, gl.DEPTH_COMPONENT16, width, height);
    gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.RENDERBUFFER, rBuffer);
    return rBuffer;
};
/**
 * Create a 2D texture that will sample from a framebuffer.
 */
exports.texture2DFromFramebuffer = (gl, frameBuffer, width, height) => {
    const texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.bindFramebuffer(gl.FRAMEBUFFER, frameBuffer);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, width, height, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);
    return texture;
};
/**
 * Generate a cube texture which will sample from a cube of framebuffers.
 */
exports.cubeTextureFromFramebuffer = (gl, frameBuffers, width, height) => {
    const texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_CUBE_MAP, texture);
    gl.texParameteri(gl.TEXTURE_CUBE_MAP, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_CUBE_MAP, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_CUBE_MAP, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_CUBE_MAP, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    for (const cf of math_1.cubeFaces()) {
        gl.bindFramebuffer(gl.FRAMEBUFFER, frameBuffers[cf]);
        gl.texImage2D(glTexCubeMapFaces[cf], 0, gl.RGBA, width, height, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
        gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, glTexCubeMapFaces[cf], texture, 0);
    }
    return texture;
};
/**
 * Draws primitives to the bound buffers.
 */
exports.glRender = (gl, nVertices, clear, viewport, mode, drawElements) => {
    gl.clearColor(0, 0, 0, 1);
    if (clear) {
        gl.clearDepth(gl.getParameter(gl.DEPTH_CLEAR_VALUE));
        gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
    }
    gl.viewport(...viewport);
    if (drawElements) {
        gl.drawElements(mode, nVertices, gl.UNSIGNED_SHORT, 0);
    }
    else {
        gl.drawArrays(mode, 0, nVertices);
    }
};
